// This has to be the first import for Firefox to work correctly
import 'construct-style-sheets-polyfill';
// Must precede the @adornis framework imports so their runtimes resolve "en". See force-english.ts.
import './force-english.js';
import { getLocale } from '../localization/out/runtime.js';

import { useABAC } from '@adornis/abac/client/useABAC.js';
import { createPermissionPayload, getRolesForUser } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import '@adornis/chemistry/elements/components/x-icon.css';
import { createERMRouter } from '@adornis/entity-relationship-management/client/erm-router.js';
import '@adornis/fonts/fontSrc/lato.css';
import { provideGlobalContext } from '@adornis/functional-lit/directive-provide-context.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful, useMemo, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { translationContext } from '@adornis/internationalization/client/translation-context.js';
import '@adornis/popover/x-dropdown.css';
import { currentUser, subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { LMSAction } from '@nsc/simple-lms/init.js';
import { PasswordEnforcementGate } from '@nsc/smartteams-shared/client/password-enforcement.js';
import { html, nothing } from 'lit';
import '../db/index.js';
import { subscribeUsersPaginated } from '../api/scoped-queries.js';
import { softDeleteUser } from '../api/soft-delete.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { ROUTE_PERMISSION_MAP, SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';
import { m } from '../localization/out/messages.js';
import { APP_VERSION } from '../version.js';
import smartteamLogo from './assets/smartteam-logo.png';
import { appAccessSection } from './components/app-access-section.js';
import { authenticationSection } from './components/authentication-section.js';
import { customerAssignmentSection } from './components/customer-assignment-editor.js';
import { companyAssignmentSection, roleSection } from './components/partner-company-assignment-editor.js';
import { Topbar } from './components/topbar.js';
import { showCreateUserDialog } from './dialogs/index.js';
import {
  availabilityManagerConfig,
  createPartnerCompanyManagerConfig,
  courseManagerConfig,
  customerManagerConfig,
  fieldEngineerManagerConfig,
} from './entity-configs/index.js';
import { confirmAndSoftDelete } from './entity-configs/soft-delete.js';
import { AvailabilityCalendar } from './pages/availability-calendar.js';
import { FieldEngineersImportExportPage } from './pages/field-engineers-import-export.js';
import './tailwind.css' with { type: 'tailwind' };

// resolve + persist the locale cookie before any engine package runtime falls back to its own baseLocale
getLocale();

BaseQLClient.initDefault();
void subscribeCurrentUser();

// Main app with ERMRouter
const SmartTeamsApp = stateful(() => {
  const permissionRoutes = [
    { route: '/partner-companies', key: 'accessCompanies' },
    { route: '/field-engineers', key: 'accessFieldEngineers' },
    { route: '/customers', key: 'accessCustomers' },
    { route: '/users', key: 'accessUsers' },
    { route: '/calendar', key: 'accessCalendar' },
    { route: '/courses', key: 'accessCourses' },
    { route: '/availabilities', key: 'accessAvailabilities' },
    { route: '/field-engineers-import-export', key: 'accessFEImportExport' },
  ];

  const can: { [key in (typeof permissionRoutes)[number]['key']] } = Object.fromEntries(
    permissionRoutes.map(({ route, key }) => [
      key,
      useABAC(accessController, createPermissionPayload(ROUTE_PERMISSION_MAP[route])).can,
    ]),
  );

  // NSC Admin only: gates the partner company "Service desk email" section and the fields it loads.
  const { can: canManageServiceDesk } = useABAC(
    accessController,
    createPermissionPayload(SmartTeamsPermission.ServiceDeskSettingsManage),
  );

  const user = useObservable(() => currentUser(), []);
  const { value: userRoles } = useQuery(
    () => getRolesForUser(user?._id ?? '')({ roleName: 1, permissions: 1 }),
    [user],
  );
  const entities = [
    createPartnerCompanyManagerConfig({ canManageServiceDesk, canSyncFieldEngineers: can.accessFEImportExport }),
    customerManagerConfig,
    fieldEngineerManagerConfig,
    availabilityManagerConfig,
    courseManagerConfig,
  ];

  const ermRouter = useMemo(
    () => [
      createERMRouter({
        entities,
        routes: [
          {
            path: 'calendar',
            label: 'Calendar',
            icon: 'icon-[mdi--calendar-month]',
            menuTitle: 'Overview',
            render: () => AvailabilityCalendar({}),
            canAccess: () => can.accessCalendar,
          },
          {
            path: 'field-engineers-import-export',
            label: 'FE Import / Export',
            icon: 'icon-[mdi--database-import-outline]',
            menuTitle: 'Modules',
            render: () => FieldEngineersImportExportPage({}),
            canAccess: () => can.accessFEImportExport,
          },
        ],
        adminPages: can.accessUsers
          ? {
              userManagement: {
                onCreate: async () => {
                  const newUserId = await showCreateUserDialog();
                  return newUserId ?? '';
                },
                list: {
                  // Custom operation hides soft-deleted users; columnName fixes the untranslated
                  // `field.email` header.
                  operation: subscribeUsersPaginated,
                  columns: [{ fieldName: 'email', columnName: 'Email' }],
                },
                detail: {
                  sections: [
                    { title: 'Master data', fields: [{ fieldName: 'email' }, { fieldName: 'username' }] },
                    authenticationSection,
                    appAccessSection,
                    roleSection,
                    companyAssignmentSection,
                    customerAssignmentSection,
                  ],
                  onDelete: (entity: AdornisUser) =>
                    confirmAndSoftDelete({
                      entityLabel: 'user',
                      name: entity.email ?? entity.username,
                      run: () => softDeleteUser(entity._id)(),
                    }),
                },
              },
              roleManagement: {
                permissions: (Object.values(SmartTeamsPermission) as string[]).concat(Object.values(LMSAction)),
                m,
              },
            }
          : undefined,
      }).init(),
    ],
    [JSON.stringify(Object.values(can)), JSON.stringify(user?._id), canManageServiceDesk],
  );

  // ERM outlet already includes sidebar + content via outletWrapper
  return html`
    <div class="h-screen overflow-y-auto flex flex-col">
      ${user ? Topbar({ user, userRoles: userRoles ?? [], version: APP_VERSION }) : nothing}
      <div class="flex-1 min-h-0">${ermRouter.outlet.value}</div>
    </div>
  `;
});

provideGlobalContext(translationContext, m);

void initApp(() =>
  PasswordEnforcementGate({
    appContent: () => SmartTeamsApp(),
    changePasswordPath: '/change-password',
    appType: 'admin',
    logoSrc: smartteamLogo,
    version: APP_VERSION,
  }),
);
